import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../lib/api';

export default function UsersPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });

  const loadAdmins = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.getAll();
      setAdmins(res.data?.data || []);
    } catch (_err) {
      setError('فشل تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const createAdmin = async (e) => {
    e.preventDefault();
    await adminAPI.create(form);
    setForm({ name: '', email: '', password: '', role: 'admin' });
    await loadAdmins();
  };

  const toggleActive = async (admin) => {
    await adminAPI.update(admin._id || admin.id, { isActive: !admin.isActive });
    await loadAdmins();
  };

  const removeAdmin = async (admin) => {
    if (!window.confirm(`حذف ${admin.name}؟`)) return;
    await adminAPI.delete(admin._id || admin.id);
    await loadAdmins();
  };

  const filteredAdmins = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return admins;
    return admins.filter((admin) =>
      [admin.name, admin.email, admin.role].some((field) => String(field || '').toLowerCase().includes(needle))
    );
  }, [admins, query]);

  const formatRole = (r) => ({ admin: 'مشرف', moderator: 'وسيط', super_admin: 'مشرف أعلى' }[r] || r);
  const totalAdmins = admins.length;
  const activeAdmins = admins.filter((admin) => admin.isActive).length;
  const inactiveAdmins = totalAdmins - activeAdmins;
  const roleCounts = admins.reduce((acc, admin) => {
    const role = admin.role || 'unknown';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 overflow-auto">
      <div className="p-4 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-[22px] font-semibold text-dark_text_1">المستخدمون</h2>
            <p className="text-sm text-dark_text_2 mt-1">إدارة المشرفين للنظام</p>
          </div>
          <button onClick={loadAdmins} className="px-3 py-1.5 rounded-lg bg-dark_bg_3 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm font-medium">
            تحديث
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">إجمالي المشرفين</p>
            <p className="mt-2 text-3xl text-dark_text_1 font-semibold">{totalAdmins}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">النشطون</p>
            <p className="mt-2 text-3xl text-emerald-300 font-semibold">{activeAdmins}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">المعطّلون</p>
            <p className="mt-2 text-3xl text-amber-300 font-semibold">{inactiveAdmins}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">الأدوار</p>
            <p className="mt-2 text-sm text-dark_text_1 truncate">
              {Object.entries(roleCounts).length === 0
                ? 'N/A'
                : Object.entries(roleCounts).map(([role, count]) => `${formatRole(role)}: ${count}`).join(' | ')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <form onSubmit={createAdmin} className="xl:col-span-2 card p-3 flex flex-wrap items-center gap-2">
            <input className="input flex-1 min-w-[120px] py-1.5" placeholder="الاسم" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
            <input className="input flex-1 min-w-[140px] py-1.5" type="email" placeholder="البريد الإلكتروني" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required />
            <input className="input flex-1 min-w-[100px] py-1.5" type="password" placeholder="كلمة المرور" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} required />
            <select className="input w-auto min-w-[100px] py-1.5" value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}>
              <option value="admin">مشرف</option>
              <option value="moderator">وسيط</option>
              <option value="super_admin">مشرف أعلى</option>
            </select>
            <button type="submit" className="px-3 py-1.5 rounded-lg bg-green_1 hover:bg-green_2 text-white text-sm font-medium shrink-0">إضافة</button>
          </form>

          <div className="card p-3">
            <p className="text-sm text-dark_text_2 mb-2">البحث عن المستخدمين</p>
            <input
              className="input"
              placeholder="البحث بالاسم أو البريد أو الدور"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <p className="text-xs text-dark_text_2 mt-3">
              عرض {filteredAdmins.length} من {totalAdmins} مشرف
            </p>
          </div>
        </div>

        <div className="card overflow-hidden">
          {loading ? <p className="p-4 text-dark_text_2">جاري التحميل...</p> : error ? <p className="p-4 text-red-400">{error}</p> : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="dark:bg-dark_bg_3">
                <tr>
                  <th className="text-start p-3 text-dark_text_2">الاسم</th>
                  <th className="text-start p-3 text-dark_text_2">البريد</th>
                  <th className="text-start p-3 text-dark_text_2">الدور</th>
                  <th className="text-start p-3 text-dark_text_2">الحالة</th>
                  <th className="text-start p-3 text-dark_text_2">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredAdmins.map((admin) => (
                  <tr key={admin._id || admin.id} className="border-t dark:border-dark_border_1">
                    <td className="p-3 text-dark_text_1 font-medium">{admin.name}</td>
                    <td className="p-3 text-dark_text_1">{admin.email}</td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-1 rounded-full dark:bg-dark_bg_4 text-dark_text_1">
                        {formatRole(admin.role)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${admin.isActive ? 'bg-green_3 text-dark_text_1' : 'dark:bg-dark_bg_4 text-dark_text_2'}`}>
                        {admin.isActive ? 'نشط' : 'معطّل'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button onClick={() => toggleActive(admin)} className="px-2 py-1 rounded text-sm font-medium bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1">
                          {admin.isActive ? 'تعطيل' : 'تفعيل'}
                        </button>
                        <button onClick={() => removeAdmin(admin)} className="px-2 py-1 rounded text-sm font-medium bg-red-600 hover:bg-red-500 text-white">
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredAdmins.length === 0 && (
                  <tr className="border-t dark:border-dark_border_1">
                    <td className="p-6 text-center text-dark_text_2" colSpan={5}>
                      لا يوجد مشرفون يطابقون البحث.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
